import React from "react";
import { Link } from "react-router-dom";
import { Shield, FileText, RotateCcw, Trash2, Mail, ChevronRight, Lock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { PRIVACY_POLICY_URL, SUPPORT_EMAIL } from "../lib/legalLinks";

const Row = ({ icon, title, desc, to, href, testid, danger }) => {
  const body = (
    <div style={{
      display: "flex", alignItems: "center", gap: "14px", padding: "16px 18px",
      background: "rgba(255,255,255,0.03)",
      border: `1px solid ${danger ? "rgba(239,68,68,0.28)" : "rgba(255,255,255,0.08)"}`,
      borderRadius: "14px", transition: "background-color 0.2s ease, border-color 0.2s ease",
    }}>
      <span style={{ color: danger ? "#fca5a5" : "#FFD166", display: "flex", flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", color: danger ? "#fca5a5" : "#fff", fontSize: "0.95rem", fontWeight: 600 }}>{title}</span>
        <span style={{ display: "block", color: "rgba(255,255,255,0.45)", fontSize: "0.8rem", marginTop: "3px", lineHeight: 1.5 }}>{desc}</span>
      </span>
      <ChevronRight size={16} style={{ color: "rgba(255,255,255,0.3)", flexShrink: 0 }} />
    </div>
  );
  const style = { textDecoration: "none", display: "block", marginBottom: "12px" };
  return href
    ? <a data-testid={testid} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" style={style}>{body}</a>
    : <Link data-testid={testid} to={to} style={style}>{body}</Link>;
};

export default function AccountPrivacyPage() {
  const { user } = useAuth();

  return (
    <main data-testid="account-privacy-page" style={{ minHeight: "100vh", background: "#120021", paddingTop: "90px" }}>
      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "0 18px 90px" }}>
        <div style={{ textAlign: "center", marginBottom: "26px" }}>
          <Lock size={22} style={{ color: "#FFD166" }} />
          <h1 className="font-cinzel gradient-text-gold" style={{ fontSize: "clamp(1.6rem, 4vw, 2.1rem)", margin: "10px 0 6px" }}>
            Account &amp; Privacy
          </h1>
          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.86rem" }}>
            Everything about your data, in one place.
          </p>
        </div>

        {user && (
          <div data-testid="account-privacy-identity" className="glass-card" style={{ padding: "18px", marginBottom: "22px" }}>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Signed in as</p>
            <p style={{ color: "#fff", fontSize: "0.98rem", fontWeight: 600 }}>{user.name}</p>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", marginTop: "2px" }}>{user.email || user.phone || ""}</p>
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", marginTop: "10px", lineHeight: 1.6 }}>
              We store your name, contact detail and birth details to calculate your Kundli, plus your questions
              and reports so you can return to them. We never sell your data or use it for advertising.
            </p>
          </div>
        )}

        <Row testid="account-privacy-policy" icon={<Shield size={19} />} title="Privacy Policy"
          desc="What we collect, who processes it, how long we keep it" to={PRIVACY_POLICY_URL} />
        <Row testid="account-privacy-terms" icon={<FileText size={19} />} title="Terms & Conditions"
          desc="The rules for using AstroFate's readings and consultations" to="/terms" />
        <Row testid="account-privacy-refunds" icon={<RotateCcw size={19} />} title="Refunds & Cancellations"
          desc="When a reading or consultation can be refunded" to="/refunds" />
        <Row testid="account-privacy-contact" icon={<Mail size={19} />} title="Contact support"
          desc={SUPPORT_EMAIL} to="/contact" />
        <Row testid="account-privacy-delete" icon={<Trash2 size={19} />} title="Delete my account and data"
          desc="Permanently removes your profile, birth details, reports and chats" to="/account/delete" danger />

        <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.76rem", textAlign: "center", marginTop: "22px", lineHeight: 1.7 }}>
          AstroFate does not access your contacts, photos, files or precise location.
        </p>
      </div>
    </main>
  );
}
